import { useMemo } from "react";
import {
  Compass,
  Stack,
  Notebook,
  ShieldCheck,
  GitBranch,
  Gauge,
  CheckCircle,
} from "@phosphor-icons/react";
import MPCycleInboxBanner from "./MPCycleInboxBanner";

/**
 * RoadmapDashboardOverview — default content for the right pane when no
 * item is selected. Shows:
 *   - Next milestone / active cycles / currently locked (from STATE)
 *   - Counts per item type (after filters applied)
 *   - Per-agent activity rollup (counts only — replaces retired AgentSidebar)
 *   - Top 3 MP-rec items as a quick-scan list
 *
 * Phase 12E (cycle-5c+25-fix8). Option C answer to Q2.
 */

const TYPE_STAT = [
  { type: "promise",    label: "Promises",    icon: Stack,        color: "hsl(var(--primary))" },
  { type: "workbook",   label: "Workbooks",   icon: Notebook,     color: "hsl(var(--status-warning))" },
  { type: "safety_net", label: "Safety-nets", icon: ShieldCheck,  color: "hsl(var(--status-critical))" },
  { type: "feature",    label: "Features",    icon: GitBranch,    color: "hsl(var(--foreground))" },
  { type: "activity",   label: "Activity",    icon: Gauge,        color: "hsl(var(--muted-foreground))" },
  { type: "ledger",     label: "Decided",     icon: CheckCircle,  color: "hsl(var(--status-success))" },
];

export default function RoadmapDashboardOverview({
  state,
  items,
  totalCount,
  allItems,
  onSelect,
  cycleInbox,
  onCycleAcknowledged,
}) {
  const typeCounts = useMemo(() => {
    const counts = {};
    for (const it of items) counts[it.type] = (counts[it.type] || 0) + 1;
    return counts;
  }, [items]);

  const agentCounts = useMemo(() => {
    const counts = {};
    for (const it of items) {
      for (const ag of it.agents) {
        counts[ag] = (counts[ag] || 0) + 1;
      }
    }
    return counts;
  }, [items]);

  const top3 = useMemo(() => items.slice(0, 3), [items]);

  // cycle-5c+25-fix25 (Owner-direct 2026-05-26): "53 of 103 items" included
  // closed items in the denominator. Owner-direct correction: denominator
  // should exclude closed; show closed as a separate "archived" annotation
  // so the historical record stays visible but doesn't inflate the workload
  // count. Falls back to `totalCount` (which is allItems.length) if the
  // parent didn't pass the full `allItems` array.
  const openTotal = useMemo(() => {
    if (!Array.isArray(allItems)) return totalCount;
    return allItems.filter((it) => it.status !== "closed").length;
  }, [allItems, totalCount]);
  const closedCount = useMemo(() => {
    if (!Array.isArray(allItems)) return 0;
    return allItems.filter((it) => it.status === "closed").length;
  }, [allItems]);

  return (
    <>
      {/* cycle-5c+25-fix21 — MP cycle-inbox banner. Self-hides when total_new === 0. */}
      <MPCycleInboxBanner inbox={cycleInbox} onAcknowledged={onCycleAcknowledged} />
      <div className="surface-card p-3 sm:p-5" data-testid="roadmap-dashboard-overview">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{ borderColor: "hsl(var(--border))" }}>
          <Compass size={18} style={{ color: "hsl(var(--primary))" }} />
          <h2
            className="text-lg font-semibold"
            style={{ color: "hsl(var(--foreground))" }}
          >
            Roadmap overview
          </h2>
          <span
            className="text-mono ml-1"
            style={{ fontSize: "0.7rem", color: "hsl(var(--muted-foreground))" }}
            data-testid="roadmap-dashboard-counts"
          >
            {items.length} of {openTotal} open shown
            {closedCount > 0 && (
              <>
                {" · "}
                <span style={{ color: "hsl(var(--muted-foreground) / 0.7)" }}>
                  {closedCount} closed archived
                </span>
              </>
            )}
          </span>
        </div>

      {state && (
        <div className="grid gap-3 md:grid-cols-3 mb-5">
          <div>
            <span
              className="text-mono"
              style={{
                fontSize: "0.6rem",
                color: "hsl(var(--muted-foreground))",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 700,
              }}
            >
              Next milestone
            </span>
            <p
              className="text-xs mt-1 leading-relaxed line-clamp-4"
              style={{ color: "hsl(var(--foreground))" }}
            >
              {state.next_milestone || "—"}
            </p>
          </div>
          <div>
            <span
              className="text-mono"
              style={{
                fontSize: "0.6rem",
                color: "hsl(var(--muted-foreground))",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 700,
              }}
            >
              Active cycles
            </span>
            <p
              className="text-xs mt-1 leading-relaxed line-clamp-4"
              style={{ color: "hsl(var(--foreground))" }}
            >
              {state.active_cycles || "—"}
            </p>
          </div>
          <div>
            <span
              className="text-mono"
              style={{
                fontSize: "0.6rem",
                color: "hsl(var(--muted-foreground))",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 700,
              }}
            >
              Currently locked
            </span>
            <p
              className="text-xs mt-1 leading-relaxed line-clamp-4"
              style={{ color: "hsl(var(--foreground))" }}
            >
              {state.tldr || "—"}
            </p>
          </div>
        </div>
      )}

      <div className="mb-5">
        <h3
          className="text-sm font-semibold mb-2"
          style={{ color: "hsl(var(--foreground))" }}
        >
          By type
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TYPE_STAT.map((t) => {
            const Icon = t.icon;
            const count = typeCounts[t.type] || 0;
            return (
              <div
                key={t.type}
                className="rounded-md border px-3 py-2 flex items-center gap-2"
                style={{
                  borderColor: "hsl(var(--border))",
                  background: "hsl(var(--background))",
                  opacity: count === 0 ? 0.5 : 1,
                }}
                data-testid={`dashboard-type-stat-${t.type}`}
              >
                <Icon size={14} style={{ color: t.color }} />
                <div className="min-w-0">
                  <div
                    className="text-mono"
                    style={{ fontSize: "0.55rem", color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}
                  >
                    {t.label}
                  </div>
                  <div
                    className="text-mono"
                    style={{ fontSize: "0.95rem", color: t.color, fontWeight: 700 }}
                  >
                    {count}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {Object.keys(agentCounts).length > 0 && (
        <div className="mb-5">
          <h3
            className="text-sm font-semibold mb-2"
            style={{ color: "hsl(var(--foreground))" }}
          >
            By agent
          </h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(agentCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([ag, count]) => (
                <span
                  key={ag}
                  className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1"
                  style={{
                    background: "hsl(var(--background))",
                    borderColor: "hsl(var(--border))",
                  }}
                  data-testid={`dashboard-agent-stat-${ag}`}
                >
                  <span
                    className="text-mono"
                    style={{ fontSize: "0.65rem", color: "hsl(var(--foreground))", fontWeight: 700 }}
                  >
                    {ag.toUpperCase()}
                  </span>
                  <span
                    className="text-mono"
                    style={{ fontSize: "0.7rem", color: "hsl(var(--primary))", fontWeight: 700 }}
                  >
                    {count}
                  </span>
                </span>
              ))}
          </div>
        </div>
      )}

      {top3.length > 0 && (
        <div>
          <h3
            className="text-sm font-semibold mb-2"
            style={{ color: "hsl(var(--foreground))" }}
          >
            Top 3 to look at
          </h3>
          <div className="space-y-2">
            {top3.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => onSelect(it.id)}
                className="w-full text-left rounded-md border px-3 py-2 hover:border-[hsl(var(--primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                style={{
                  background: "hsl(var(--background))",
                  borderColor: "hsl(var(--border))",
                }}
                data-testid={`dashboard-top3-${it.id}`}
              >
                <div
                  className="text-mono mb-0.5"
                  style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}
                >
                  {it.type} {it.tier ? `· ${it.tier}` : ""}
                </div>
                <div
                  className="text-sm font-semibold"
                  style={{ color: "hsl(var(--foreground))" }}
                >
                  {it.title}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
